import { FC, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";
import ModalAddPromoCode from "./ModalAddPromoCode";
import {
  discountLabel,
  TPromoCode,
  useArchivePromoCode,
  useGetPromoCodes,
  useTogglePromoCode,
} from "../../api/billingAdmin";

/**
 * Promo code list.
 *
 * Codes were previously created by hand in the Stripe Dashboard, with no local
 * record and therefore no way to see usage or restrict a code to a plan.
 */
export const BillingPromoTab: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "redeemable" | "expired">("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [addOpen, setAddOpen] = useState(false);

  const { promoCodes, total, isLoading } = useGetPromoCodes({
    search: search || undefined,
    status: status || undefined,
    page: page + 1, // the API paginator is 1-based
    per_page: perPage,
  });

  const togglePromo = useTogglePromoCode();
  const archivePromo = useArchivePromoCode();

  const handleArchive = (promo: TPromoCode) => {
    // Archiving deactivates the code in Stripe and soft-deletes it here. It is
    // not reversible from this screen, so it gets a confirmation.
    const confirmed = window.confirm(
      t(
        "billing.confirmArchive",
        'Archiver le code "{{code}}" ? Il ne pourra plus être utilisé.',
        { code: promo.code }
      )
    );
    if (confirmed) archivePromo.mutate(promo.id);
  };

  return (
    <>
      <div className={classes.filterBar}>
        <TextField
          className={classes.filterControl}
          size="small"
          label={t("common.search", "Rechercher")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />

        <FormControl className={classes.filterControl} size="small">
          <InputLabel>{t("common.status", "Statut")}</InputLabel>
          <Select
            label={t("common.status", "Statut")}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as any);
              setPage(0);
            }}
          >
            <MenuItem value="">{t("common.all", "Tous")}</MenuItem>
            <MenuItem value="redeemable">
              {t("billing.redeemable", "Utilisables")}
            </MenuItem>
            <MenuItem value="expired">
              {t("billing.expired", "Expirés")}
            </MenuItem>
          </Select>
        </FormControl>

        <div style={{ flex: "1 1 auto" }} />

        <Button variant="contained" onClick={() => setAddOpen(true)}>
          {t("billing.newPromoCode", "Nouveau code promo")}
        </Button>
      </div>

      <div className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("billing.code", "Code")}</TableCell>
              <TableCell>{t("billing.discount", "Remise")}</TableCell>
              <TableCell>{t("billing.duration", "Durée")}</TableCell>
              <TableCell>{t("billing.usage", "Utilisations")}</TableCell>
              <TableCell>{t("billing.expiresAt", "Expire le")}</TableCell>
              <TableCell>{t("common.active", "Actif")}</TableCell>
              <TableCell align="right">
                {t("common.actions", "Actions")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className={classes.emptyState}>
                    <CircularProgress size={24} />
                  </div>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && promoCodes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className={classes.emptyState}>
                    {t("billing.noPromoCodes", "Aucun code promo")}
                  </div>
                </TableCell>
              </TableRow>
            )}

            {promoCodes.map((promo) => (
              <TableRow key={promo.id}>
                <TableCell>
                  <span className={classes.codeCell}>{promo.code}</span>
                  {promo.name && (
                    <div className={classes.planSlug}>{promo.name}</div>
                  )}
                </TableCell>

                <TableCell>{discountLabel(promo)}</TableCell>

                <TableCell>
                  {promo.duration === "once" &&
                    t("billing.durationOnce", "Une seule facture")}
                  {promo.duration === "forever" &&
                    t("billing.durationForever", "À vie")}
                  {promo.duration === "repeating" &&
                    t("billing.durationMonthsShort", "{{count}} mois", {
                      count: promo.duration_in_months ?? 0,
                    })}
                </TableCell>

                <TableCell>
                  {promo.times_redeemed}
                  {promo.max_redemptions ? ` / ${promo.max_redemptions}` : ""}
                </TableCell>

                <TableCell>
                  {promo.expires_at
                    ? new Date(promo.expires_at).toLocaleDateString()
                    : "—"}
                </TableCell>

                <TableCell>
                  <Switch
                    size="small"
                    checked={promo.is_active}
                    onChange={() => togglePromo.mutate(promo.id)}
                    disabled={togglePromo.isPending}
                    inputProps={{ "aria-label": `toggle ${promo.code}` }}
                  />
                </TableCell>

                <TableCell align="right">
                  <div
                    className={classes.rowActions}
                    style={{ justifyContent: "flex-end" }}
                  >
                    {promo.first_time_only && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t("billing.firstTimeShort", "1er abo.")}
                      />
                    )}
                    <Tooltip title={t("common.archive", "Archiver") as string}>
                      <span>
                        <IconButton
                          size="small"
                          className={classes.deleteButton}
                          onClick={() => handleArchive(promo)}
                          disabled={archivePromo.isPending}
                          aria-label={`archive ${promo.code}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={perPage}
        rowsPerPageOptions={[10, 25, 50]}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      <ModalAddPromoCode open={addOpen} onClose={() => setAddOpen(false)} />
    </>
  );
};

export default BillingPromoTab;
